import { Link } from "react-router-dom";
import { MessageSquare } from "lucide-react";
import type { Thread } from "@/api/types";
import { timeAgo, cn } from "@/lib/utils";
import { ClientBadge } from "./ClientBadge";

export function threadTitle(t: Thread): string {
  if (t.title) return t.title;
  const names = (t.notes ?? []).map((n) => n.title).filter(Boolean) as string[];
  if (names.length === 0) return "Untitled thread";
  const shown = names.slice(0, 3).join(", ");
  return names.length > 3 ? `${shown} +${names.length - 3}` : shown;
}

export function ThreadCard({ thread, className }: { thread: Thread; className?: string }) {
  const notes = thread.notes ?? [];
  return (
    <Link
      to={`/threads/${thread.id}`}
      className={cn(
        "group flex items-start gap-3 rounded-lg px-3 py-2.5 transition-colors hover:bg-muted/60 focus-visible:bg-muted/60 focus-visible:outline-none",
        className,
      )}
    >
      <MessageSquare className="mt-1 size-4 shrink-0 text-muted-foreground" aria-hidden />
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline justify-between gap-3">
          <span className="truncate text-[0.95rem] font-medium">{threadTitle(thread)}</span>
          <span className="shrink-0 text-xs text-muted-foreground" title={thread.lastWriteAt}>
            {timeAgo(thread.lastWriteAt)}
          </span>
        </div>
        {thread.summary && <p className="mt-0.5 line-clamp-2 text-sm text-muted-foreground">{thread.summary}</p>}
        <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
          <ClientBadge client={thread.clientId} name={thread.clientName} />
          {notes.length > 0 && (
            <span>
              {notes.length} {notes.length === 1 ? "note" : "notes"}
            </span>
          )}
        </div>
      </div>
    </Link>
  );
}
